import { Button } from '@/components/ui/button'
import { Dropzone } from '@/components/ui/dropzone'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import { FileSpreadsheet, FolderUp, X } from 'lucide-react'
import {
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react'

export type FileDropzoneProps = {
  accept?: string
  className?: string
  description?: ReactNode
  disabled?: boolean
  file?: File | null
  id?: string
  label?: ReactNode
  onClear?: () => void
  onFileSelect?: (file: File) => void
}

function formatFileSize(size: number) {
  if (!Number.isFinite(size) || size <= 0) {
    return '0 KB'
  }

  if (size < 1024 * 1024) {
    return `${Math.max(1, Math.round(size / 1024))} KB`
  }

  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

export function FileDropzone({
  accept,
  className,
  description,
  disabled = false,
  file,
  id,
  label,
  onClear,
  onFileSelect,
}: FileDropzoneProps) {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const descriptionId = description ? `${inputId}-description` : undefined
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  const openFilePicker = () => {
    if (disabled) return

    inputRef.current?.click()
  }

  const handleSelectedFiles = (files: FileList | null) => {
    const nextFile = files?.[0]

    if (!nextFile || disabled) {
      return
    }

    onFileSelect?.(nextFile)
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleSelectedFiles(event.target.files)
    event.target.value = ''
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)
    handleSelectedFiles(event.dataTransfer.files)
  }

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()

    if (!disabled) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) {
      return
    }

    setIsDragging(false)
  }

  const handleZoneKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    openFilePicker()
  }

  return (
    <div className={cn('space-y-3', className)}>
      {label ? (
        <label htmlFor={inputId} className="text-sm font-medium">
          {label}
        </label>
      ) : null}

      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        className="sr-only"
        aria-describedby={descriptionId}
        disabled={disabled}
        onChange={handleInputChange}
      />

      <Dropzone
        role="button"
        tabIndex={disabled ? -1 : 0}
        disabled={disabled}
        active={isDragging}
        onClick={(event: MouseEvent<HTMLDivElement>) => {
          if (
            event.target instanceof Element &&
            event.target.closest('[data-file-dropzone-action]')
          ) {
            return
          }

          openFilePicker()
        }}
        onKeyDown={handleZoneKeyDown}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        className="cursor-pointer p-5"
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="inline-flex size-11 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-card text-interaction">
              {file ? <FileSpreadsheet className="size-5" /> : <FolderUp className="size-5" />}
            </span>

            <div className="space-y-1">
              <p className="font-medium leading-6">{file ? file.name : 'Click to select a file'}</p>
              <Typography variant="supporting" className="block">
                {file
                  ? `${formatFileSize(file.size)} selected for preview and import.`
                  : 'Or drag and drop a supported file here.'}
              </Typography>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-file-dropzone-action
              onClick={(event) => {
                event.stopPropagation()
                openFilePicker()
              }}
              disabled={disabled}
            >
              {file ? 'Replace File' : 'Choose File'}
            </Button>

            {file ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                data-file-dropzone-action
                onClick={(event) => {
                  event.stopPropagation()
                  onClear?.()
                }}
                disabled={disabled}
              >
                <X className="size-4" />
                Clear
              </Button>
            ) : null}
          </div>
        </div>
      </Dropzone>

      {description ? (
        <Typography variant="supporting" id={descriptionId} className="block">
          {description}
        </Typography>
      ) : null}
    </div>
  )
}
